// Build sidebar TOC (Overview + all H2s) for "block-hero-profile-thought-leadership-b"
const DEFAULT_OFFSET = 130;

const slugify = (text) =>
	text
		.toString()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9\s-]/g, '')
		.replace(/\s+/g, '-')
		.replace(/-+/g, '-');

function buildTocForContainer(container) {
	const sidebar = container.querySelector(
		'.block-hero-profile-thought-leadership-b__sidebar'
	);
	const contentWrapper = container.querySelector(
		'.block-hero-profile-thought-leadership-b__content-wrapper'
	);
	if (!sidebar || !contentWrapper) return;

	const section = container.closest('section');
	const offset = Number(section?.dataset?.offset || DEFAULT_OFFSET);

	// --- Gather targets: Overview + all H2s
	const targets = [];
	const firstParagraph = contentWrapper.querySelector('p');
	const overviewEl = firstParagraph || contentWrapper;

	const usedIds = new Set(
		[...document.querySelectorAll('[id]')].map((n) => n.id)
	);
	let overviewId = 'overview',
		n = 2;
	while (usedIds.has(overviewId)) overviewId = `overview-${n++}`;
	if (!overviewEl.id) overviewEl.id = overviewId;
	overviewEl.style.scrollMarginTop = `${offset + 10}px`;
	targets.push({ el: overviewEl, id: overviewEl.id, label: 'Overview' });

	const h2s = Array.from(contentWrapper.querySelectorAll('h2'));
	usedIds.add(overviewEl.id);
	h2s.forEach((h2, i) => {
		if (!h2.id) {
			const base =
				slugify(h2.textContent || `section-${i + 1}`) ||
				`section-${i + 1}`;
			let id = base,
				k = 2;
			while (usedIds.has(id)) id = `${base}-${k++}`;
			h2.id = id;
		}
		usedIds.add(h2.id);
		h2.style.scrollMarginTop = `${offset + 10}px`;
		targets.push({
			el: h2,
			id: h2.id,
			label: (h2.textContent || 'Section').trim(),
		});
	});

	if (!targets.length) {
		sidebar.style.display = 'none';
		return;
	}

	// --- Ensure sidebar-inner exists
	let sidebarInner = sidebar.querySelector(
		'.block-hero-profile-thought-leadership-b__sidebar-inner'
	);
	if (!sidebarInner) {
		sidebarInner = document.createElement('div');
		sidebarInner.className =
			'block-hero-profile-thought-leadership-b__sidebar-inner';
		sidebar.appendChild(sidebarInner);
	}

	// Ensure title exists inside sidebar-inner
	if (
		!sidebarInner.querySelector(
			'.block-hero-profile-thought-leadership-b__sidebar-title'
		)
	) {
		const title = document.createElement('p');
		title.className =
			'block-hero-profile-thought-leadership-b__sidebar-title';
		title.textContent = 'Contents';
		sidebarInner.appendChild(title);
	}

	// --- Build / rebuild NAV *inside* sidebar-inner
	// Remove previous navs only inside inner (not elsewhere)
	sidebarInner
		.querySelectorAll(
			'.block-hero-profile-thought-leadership-b__sidebar-nav'
		)
		.forEach((nv) => nv.remove());

	const nav = document.createElement('nav');
	nav.className = 'block-hero-profile-thought-leadership-b__sidebar-nav';

	const ul = document.createElement('ul');
	ul.className = 'block-hero-profile-thought-leadership-b__sidebar-list';
	nav.appendChild(ul);

	// Append the nav right after the title (or at end of inner)
	const titleEl = sidebarInner.querySelector(
		'.block-hero-profile-thought-leadership-b__sidebar-title'
	);
	if (titleEl && titleEl.nextSibling) {
		titleEl.insertAdjacentElement('afterend', nav);
	} else {
		sidebarInner.appendChild(nav);
	}

	// --- Move share block into the nav (after the <ul>) if present
	// Prefer share that currently lives in the sidebar; fallback to any immediate child share in this block.
	let shareBlock = sidebar.querySelector(
		':scope > .acf-innerblocks-container'
	);
	if (!shareBlock) {
		// As a fallback, if a share block is already inside the inner but not in the nav, move it.
		shareBlock = sidebarInner.querySelector(
			':scope > .acf-innerblocks-container'
		);
	}
	if (shareBlock) {
		nav.appendChild(shareBlock); // this moves it after <ul>
	}

	// --- Build items into nav
	const linkById = new Map(); // id -> [links]
	function addItem(target) {
		const li = document.createElement('li');
		li.className = 'block-hero-profile-thought-leadership-b__sidebar-item';

		const a = document.createElement('a');
		a.className = 'block-hero-profile-thought-leadership-b__sidebar-link';
		a.textContent = target.label;
		a.href = `#${target.id}`;
		a.addEventListener('click', (e) => {
			e.preventDefault();
			const top =
				target.el.getBoundingClientRect().top +
				window.pageYOffset -
				offset;
			history.pushState(null, '', `#${target.id}`);
			window.scrollTo({ top, behavior: 'smooth' });
		});

		li.appendChild(a);
		ul.appendChild(li);

		const arr = linkById.get(target.id) || [];
		arr.push(a);
		linkById.set(target.id, arr);
	}

	targets.forEach(addItem);

	// --- Scroll spy for the (single) nav
	let activeId = null;
	const setActive = (id) => {
		if (activeId === id) return;
		activeId = id;
		linkById.forEach((arr) =>
			arr.forEach((a) => a.classList.remove('is-active'))
		);
		const links = linkById.get(id);
		if (links) links.forEach((a) => a.classList.add('is-active'));
	};

	const io = new IntersectionObserver(
		(entries) => {
			const vis = entries
				.filter((en) => en.isIntersecting)
				.sort(
					(a, b) =>
						Math.abs(a.boundingClientRect.top) -
						Math.abs(b.boundingClientRect.top)
				);
			if (vis.length) {
				setActive(vis[0].target.id);
			} else {
				const y = window.scrollY + offset + 1;
				let current = targets[0].id;
				for (const t of targets) {
					if (t.el.offsetTop <= y) current = t.id;
					else break;
				}
				setActive(current);
			}
		},
		{
			root: null,
			rootMargin: `-${offset}px 0px -70% 0px`,
			threshold: [0, 1],
		}
	);
	targets.forEach(({ el }) => io.observe(el));

	// Initialize active state (hash-aware)
	const initId =
		location.hash && document.getElementById(location.hash.slice(1))
			? location.hash.slice(1)
			: targets[0].id;
	setActive(initId);

	// --- MOBILE: clone the whole inner (title + nav + share) under the image wrapper
	const imageWrapper = container.querySelector(
		'.block-hero-profile-thought-leadership-b__image-wrapper'
	);
	if (imageWrapper) {
		// remove previous mobile copies
		container
			.querySelectorAll(
				'.block-hero-profile-thought-leadership-b__sidebar-inner--mobile'
			)
			.forEach((el) => el.remove());

		const mobileInner = sidebarInner.cloneNode(true);
		mobileInner.classList.add(
			'block-hero-profile-thought-leadership-b__sidebar-inner--mobile'
		);
		imageWrapper.insertAdjacentElement('afterend', mobileInner);

		// Sync active state for links inside the mobile clone as well
		// (find all anchors inside the mobile clone and map them by hash)
		const mobileLinksById = new Map();
		mobileInner
			.querySelectorAll(
				'.block-hero-profile-thought-leadership-b__sidebar-link[href^="#"]'
			)
			.forEach((a) => {
				const id = a.getAttribute('href').slice(1);
				const arr = mobileLinksById.get(id) || [];
				arr.push(a);
				mobileLinksById.set(id, arr);

				a.addEventListener('click', (e) => {
					e.preventDefault();
					const target = document.getElementById(id);
					if (target) {
						const top =
							target.getBoundingClientRect().top +
							window.pageYOffset -
							offset;
						history.pushState(null, '', `#${id}`);
						window.scrollTo({ top, behavior: 'smooth' });
					}
				});
			});

		// extend setActive to also toggle mobile links
		const prevSetActive = setActive;
		const setActiveBoth = (id) => {
			prevSetActive(id);
			// clear mobile
			mobileLinksById.forEach((arr) =>
				arr.forEach((a) => a.classList.remove('is-active'))
			);
			const m = mobileLinksById.get(id);
			if (m) m.forEach((a) => a.classList.add('is-active'));
		};

		// rebind observer callback to call both
		const observerTargets = targets.map(({ el }) => el);
		io.disconnect();
		const io2 = new IntersectionObserver(
			(entries) => {
				const vis = entries
					.filter((en) => en.isIntersecting)
					.sort(
						(a, b) =>
							Math.abs(a.boundingClientRect.top) -
							Math.abs(b.boundingClientRect.top)
					);
				if (vis.length) {
					setActiveBoth(vis[0].target.id);
				} else {
					const y = window.scrollY + offset + 1;
					let current = targets[0].id;
					for (const t of targets) {
						if (t.el.offsetTop <= y) current = t.id;
						else break;
					}
					setActiveBoth(current);
				}
			},
			{
				root: null,
				rootMargin: `-${offset}px 0px -70% 0px`,
				threshold: [0, 1],
			}
		);
		observerTargets.forEach((el) => io2.observe(el));
		setActiveBoth(initId);
	}
}

// Run after DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener(
		'DOMContentLoaded',
		() => {
			document
				.querySelectorAll(
					'.block-hero-profile-thought-leadership-b__container'
				)
				.forEach(buildTocForContainer);
		},
		{ once: true }
	);
} else {
	document
		.querySelectorAll('.block-hero-profile-thought-leadership-b__container')
		.forEach(buildTocForContainer);
}
